import './ScatterChart.scss';

import { Tooltip } from '@trackman/web-shared-components';

import Dot from 'components/Basic/Dot/Dot';
import { ScatterChartDataset, ScatterChartPoint } from 'types/CodegenReportTypes';
import Legend, { LegendItem } from 'components/Charts/Legend/Legend';
import CoordinateSystem from 'components/Charts/CoordinateSystem/CoordinateSystem';
import { memo, useMemo } from 'react';

export type GraphicalScatterChartDataset = ScatterChartDataset & {
  color: string;
};

type Props = {
  xLabel: string;
  yLabel: string;
  chartData: GraphicalScatterChartDataset[];
};

const DataPlot = memo(({ x, y, color, title }: { x: number; y: number; color: string; title: string }) => (
  <Tooltip title={title} placement='bottom' arrow>
    <div
      className='data-plot-container'
      style={{
        left: `${x}%`,
        bottom: `${y}%`,
        position: 'absolute',
      }}
    >
      <Dot color={color} />
    </div>
  </Tooltip>
));

const ScatterChart = ({ xLabel, yLabel, chartData }: Props) => {
  const { xTicks, yTicks, legendItems, plots } = useMemo(() => {
    const allPoints = chartData.flatMap((datum) => datum.points);
    const lines = getChartLines(allPoints);

    const legendItems: LegendItem[] = chartData
      .filter((datum) => datum.points.length)
      .map((datum) => ({
        label: datum.label,
        dotProps: { color: datum.color },
      }));

    const plots = chartData.flatMap((datum, i) =>
      datum.points.map((point, j) => ({
        key: `${datum.label}-${i}-${j}`,
        x: lines.xRelative(point.x),
        y: lines.yRelative(point.y),
        color: datum.color,
        title: `(${point.x.toFixed(2)}, ${point.y.toFixed(2)})`,
      }))
    );

    return { xTicks: lines.xTicks, yTicks: lines.yTicks, legendItems, plots };
  }, [chartData]);

  return (
    <div className='ScatterChart'>
      <CoordinateSystem xLabel={xLabel} yLabel={yLabel} xTicks={xTicks} yTicks={yTicks}>
        {plots.map((plot) => (
          <DataPlot key={plot.key} x={plot.x} y={plot.y} color={plot.color} title={plot.title} />
        ))}
      </CoordinateSystem>
      <Legend items={legendItems} />
    </div>
  );
};

export default ScatterChart;

const getChartLines = (allPoints: ScatterChartPoint[]) => {
  if (allPoints.length === 0)
    return {
      xTicks: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      yTicks: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      xRelative: (x: number) => x,
      yRelative: (y: number) => y,
    };
  const xMin = Math.min(...allPoints.map((point) => point.x));
  const xMax = Math.max(...allPoints.map((point) => point.x));
  const yMin = Math.min(...allPoints.map((point) => point.y));
  const yMax = Math.max(...allPoints.map((point) => point.y));
  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const xStep = calculateStepSize(xRange);
  const yStep = calculateStepSize(yRange);
  const xMinTick = Math.floor(xMin / xStep) * xStep;
  const yMinTick = Math.floor(yMin / yStep) * yStep;
  const xSteps = Math.ceil((xMax - xMinTick) / xStep) + 1;
  const ySteps = Math.ceil((yMax - yMinTick) / yStep) + 1;
  const xTicks = Array.from({ length: xSteps }, (_, i) => Number((xMinTick + i * xStep).toFixed(1)));
  const yTicks = Array.from({ length: ySteps }, (_, i) => Number((yMinTick + i * yStep).toFixed(1)));
  const xTickRange = xTicks[xTicks.length - 1] - xTicks[0];
  const yTickRange = yTicks[yTicks.length - 1] - yTicks[0];
  const xRelative = (value: number) => (xTickRange === 0 ? 0 : ((value - xTicks[0]) / xTickRange) * 100);
  const yRelative = (value: number) => (yTickRange === 0 ? 0 : ((value - yTicks[0]) / yTickRange) * 100);
  return {
    xTicks,
    yTicks,
    xRelative,
    yRelative,
  };
};

export function calculateStepSize(range: number): number {
  if (range <= 1.5) {
    return 0.1;
  } else if (range <= 3) {
    return 0.25;
  } else if (range <= 6) {
    return 0.5;
  } else if (range <= 15) {
    return 1;
  } else if (range <= 30) {
    return 2;
  } else if (range <= 75) {
    return 5;
  } else if (range <= 150) {
    return 10;
  } else if (range <= 375) {
    return 25;
  } else if (range <= 750) {
    return 50;
  } else if (range <= 1500) {
    return 100;
  } else if (range <= 3750) {
    return 250;
  } else {
    return 500;
  }
}
